import Action from '../../elements/progressBar/progressBarAction';
import Container from '../../atoms/container/progressBarContainer';
import Content from '../../elements/progressBar/progressBarContent';
import { PlayerState } from '../../../../shared/types/enums/player';
import { useAppDispatch, useAppSelector } from '../../../../shared/store/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  mediaSelector,
  setPosition as setPlayerPosition,
} from '../../../../shared/store/slices/media.slice';

function ProgressBarVod() {
  const dispatch = useAppDispatch();

  // Selector values

  const {
    currentTime,
    position: currentPosition,
    range,
    state,
  } = useAppSelector(mediaSelector);

  // Memoized values

  const {
    currentProgress,
    duration,
    endContent,
    normalizedPointer,
    normalizedPosition,
    seeking,
    startBookmark,
    startContent,
  } = useMemo(() => {
    // Range data
    const { start: startContent = 0, end: endContent = 0 } = range.seek || {};
    const { start: startBookmark = 0, end: endBookmark = 0 } =
      range.bookmarks || {};

    // Normalized data
    const normalizedPointer =
      Math.max(currentPosition - startBookmark, 0) * 1000; // Pointer position
    const normalizedPosition = Math.max(currentTime - startBookmark, 0) * 1000; // Playback position

    // Misc data
    const duration = ((endBookmark || endContent) - startBookmark) * 1000;
    const progress = (normalizedPointer / duration) * 100 || 0;
    const seeking = state === PlayerState.SEEKING;

    return {
      currentProgress: progress,
      duration,
      endBookmark,
      endContent,
      normalizedPointer,
      normalizedPosition,
      seeking,
      startContent,
      startBookmark,
    };
  }, [currentPosition, currentTime, range, state]);

  // State hooks

  const [hovering, setHovering] = useState(false);
  const [pointer, setPointer] = useState<number | undefined>(undefined);
  const [position, setPosition] = useState(normalizedPosition);
  const [progress, setProgress] = useState(currentProgress);

  // References

  const containerRef = useRef<HTMLDivElement>(null);

  // Event handlers

  const onMouseLeave = useCallback(() => setHovering(false), []);

  const onMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (event.target !== event.currentTarget) return;
      if (
        [
          PlayerState.IDLE,
          PlayerState.LOADED,
          PlayerState.SEEKING,
          PlayerState.STALLED,
        ].includes(state)
      ) {
        return;
      }
      if (containerRef.current) {
        const offset = event.nativeEvent.offsetX;
        const width = containerRef.current.offsetWidth;
        const progress = (offset / width) * 100;
        const pointer = duration * (progress / 100);
        setPointer(pointer);
        setProgress(progress);
        setHovering(true);
      }
      event.stopPropagation();
    },
    [duration, range, state]
  );

  const onPointerDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (hovering) {
        dispatch(
          setPlayerPosition({
            position: Math.min(
              (pointer || 0) / 1000 + startBookmark,
              endContent - 1
            ),
            seek: true,
          })
        );
      }
      event.stopPropagation();
    },
    [endContent, pointer, hovering, startBookmark]
  );

  // Effect hooks

  useEffect(() => {
    seeking && setHovering(false);
  }, [seeking]);

  useEffect(() => {
    if (!startContent && !endContent) {
      setPointer(undefined);
      setPosition(0);
      setProgress(0);
    }
  }, [endContent, startContent]);

  useEffect(() => {
    setPosition(normalizedPosition);
    if (!hovering) {
      setPointer(seeking ? normalizedPointer : undefined);
      setProgress(currentProgress);
    }
  }, [currentPosition, currentTime, hovering, seeking]);

  return (
    <>
      <Action />
      <Container
        onClick={onPointerDown}
        onMouseLeave={onMouseLeave}
        onMouseMove={onMouseMove}
        ref={containerRef}
        timeshift={true}
      >
        <Content
          animate={false}
          duration={duration}
          live={false}
          pointer={pointer}
          position={position}
          progress={progress}
        />
      </Container>
    </>
  );
}

export default ProgressBarVod;
